import { useEffect, useMemo, useRef, useState } from "react";
import QRCodeStyling from "qr-code-styling";
import { createOpticallyCenteredQrIconDataUrl } from "../utils/tintTransparentImage";

const getQrShapeOptions = (shape) => {
  if (shape === "square") {
    return {
      dotsType: "square",
      cornersSquareType: "square",
      cornersDotType: "square"
    };
  }

  if (shape === "circle") {
    return {
      dotsType: "dots",
      cornersSquareType: "extra-rounded",
      cornersDotType: "dot"
    };
  }

  if (shape === "diamond") {
    return {
      dotsType: "classy",
      cornersSquareType: "square",
      cornersDotType: "square"
    };
  }

  return {
    dotsType: "rounded",
    cornersSquareType: "extra-rounded",
    cornersDotType: "dot"
  };
};

function BuildQRCode({
  url,
  logoSrc,
  size = 280,
  theme = {}
}) {
  const qrRef = useRef(null);
  const [recoloredLogoSrc, setRecoloredLogoSrc] = useState("");
  const [positionedLogoSrc, setPositionedLogoSrc] = useState("");

  const dotColor = theme.dotColor || "#111111";
  const backgroundColor = theme.backgroundColor || "#ffffff";
  const cornerColor = theme.cornerColor || dotColor;
  const cornerSquareColor = theme.cornerSquareColor || cornerColor;
  const cornerDotColor = theme.cornerDotColor || cornerColor;
  const logoColor = theme.logoColor || "";
  const qrMargin = theme.margin ?? 8;
  const shape = theme.shape || "rounded";
  const logoImageSize = Math.min(0.24, 68 / size);

  useEffect(() => {
    let isMounted = true;

    const recolorLogo = () => {
      if (!logoSrc || !logoColor) {
        setRecoloredLogoSrc("");
        return;
      }

      try {
        const image = new Image();
        image.crossOrigin = "anonymous";

        image.onload = () => {
          if (!isMounted) return;

          const canvas = document.createElement("canvas");
          canvas.width = image.naturalWidth || image.width;
          canvas.height = image.naturalHeight || image.height;

          const context = canvas.getContext("2d");

          if (!context) {
            setRecoloredLogoSrc("");
            return;
          }

          context.clearRect(0, 0, canvas.width, canvas.height);
          context.drawImage(image, 0, 0, canvas.width, canvas.height);
          context.globalCompositeOperation = "source-in";
          context.fillStyle = logoColor;
          context.fillRect(0, 0, canvas.width, canvas.height);

          setRecoloredLogoSrc(canvas.toDataURL("image/png"));
        };

        image.onerror = () => {
          if (isMounted) {
            setRecoloredLogoSrc("");
          }
        };

        image.src = logoSrc;
      } catch (error) {
        console.log("QR logo recolor failed:", error);

        if (isMounted) {
          setRecoloredLogoSrc("");
        }
      }
    };

    recolorLogo();

    return () => {
      isMounted = false;
    };
  }, [logoSrc, logoColor]);

  useEffect(() => {
    let isMounted = true;
    const source = recoloredLogoSrc || logoSrc;
    setPositionedLogoSrc("");

    if (!source) return () => { isMounted = false; };

    createOpticallyCenteredQrIconDataUrl(source, size, logoImageSize)
      .then((result) => { if (isMounted) setPositionedLogoSrc(result); })
      .catch((error) => console.log("QR logo positioning failed:", error));

    return () => { isMounted = false; };
  }, [recoloredLogoSrc, logoSrc, size, logoImageSize]);

  const options = useMemo(() => {
    const imageSource = positionedLogoSrc || undefined;
    const shapeOptions = getQrShapeOptions(shape);
    const dotsType = theme.dotType || shapeOptions.dotsType;
    const cornersSquareType = theme.cornerSquareType || shapeOptions.cornersSquareType;
    const cornersDotType = theme.cornerDotType || shapeOptions.cornersDotType;

    return {
      width: size,
      height: size,
      type: "canvas",
      data: url || "",
      margin: qrMargin,
      qrOptions: {
        typeNumber: 0,
        mode: "Byte",
        errorCorrectionLevel: "H"
      },
      backgroundOptions: {
        color: backgroundColor
      },
      dotsOptions: {
        color: dotColor,
        type: dotsType
      },
      cornersSquareOptions: {
        color: cornerSquareColor,
        type: cornersSquareType
      },
      cornersDotOptions: {
        color: cornerDotColor,
        type: cornersDotType
      },
      image: imageSource,
      imageOptions: {
        crossOrigin: "anonymous",
        margin: 6,
        imageSize: logoImageSize,
        hideBackgroundDots: true
      }
    };
  }, [
    url,
    size,
    positionedLogoSrc,
    backgroundColor,
    dotColor,
    cornerSquareColor,
    cornerDotColor,
    qrMargin,
    shape,
    theme.dotType,
    theme.cornerSquareType,
    theme.cornerDotType,
    logoImageSize
  ]);

  useEffect(() => {
    if (!qrRef.current || !url) return;

    qrRef.current.innerHTML = "";

    const qrCode = new QRCodeStyling(options);
    qrCode.append(qrRef.current);
  }, [options, url]);

  return <div ref={qrRef} aria-label="Build QR code" />;
}

export default BuildQRCode;
